<script setup lang="ts">
/**
 * Code editor on CodeMirror 6: line numbers, folding, bracket matching,
 * search (Mod+F), multi-cursor, undo history, completion — styled entirely
 * from the design tokens, so it follows light and dark mode with the app.
 *
 * `v-model` is the text. `language` picks the grammar by name (`javascript`,
 * `json`, `sql`, `plain`); `extensions` adds any further CodeMirror extension (another
 * language, a linter). `completions` takes a plain list — matched against the
 * word before the caret — or a CodeMirror completion source. `errorLine`
 * marks one 1-based line (a compile error, a failed run); the mark follows
 * the text as it is edited.
 *
 * The box has a minimum and a maximum height and scrolls in between; users can
 * drag it taller. Tab indents; Escape, then Tab, leaves the editor (the
 * CodeMirror convention that keeps keyboard users from getting trapped).
 *
 * `submitOnModEnter` turns Ctrl/Cmd+Enter into `submit` (run a query, send a
 * script) instead of CodeMirror's „new line below“; off by default.
 */
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { closeBrackets, closeBracketsKeymap, completionKeymap } from '@codemirror/autocomplete';
import {
  bracketMatching,
  foldGutter,
  foldKeymap,
  indentOnInput,
  indentUnit,
  syntaxHighlighting,
} from '@codemirror/language';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import {
  Compartment,
  EditorState,
  Prec,
  StateEffect,
  StateField,
  type Extension,
} from '@codemirror/state';
import {
  Decoration,
  EditorView,
  drawSelection,
  highlightActiveLine,
  highlightActiveLineGutter,
  highlightSpecialChars,
  keymap,
  lineNumbers as lineNumbersExt,
  placeholder as placeholderExt,
  type DecorationSet,
} from '@codemirror/view';
import { onBeforeUnmount, onMounted, shallowRef, useTemplateRef, watch } from 'vue';
import {
  completionExtension,
  languageExtension,
  nxCodeHighlightStyle,
  nxCodeTheme,
  type NxCodeCompletions,
  type NxCodeLanguage,
} from './editorSetup';

const props = withDefaults(
  defineProps<{
    language?: NxCodeLanguage;
    /** Further CodeMirror extensions (another language, a linter, keymaps). */
    extensions?: readonly Extension[];
    completions?: NxCodeCompletions | null;
    readonly?: boolean;
    lineNumbers?: boolean;
    /** Wrap long lines instead of scrolling sideways. */
    wrap?: boolean;
    /** Box height bounds, as CSS lengths (tokens or `calc(…)`). */
    minHeight?: string;
    maxHeight?: string;
    placeholder?: string;
    /** Accessible name of the text area („Skript“). */
    ariaLabel?: string;
    /** 1-based line to mark as faulty, `null` for none. */
    errorLine?: number | null;
    /** Ctrl/Cmd+Enter emits `submit` instead of inserting a line below. */
    submitOnModEnter?: boolean;
  }>(),
  {
    language: 'plain',
    extensions: () => [],
    completions: null,
    readonly: false,
    lineNumbers: true,
    wrap: true,
    minHeight: 'calc(var(--nxt-size-unit) * 8)',
    maxHeight: 'calc(var(--nxt-size-unit) * 30)',
    placeholder: '',
    ariaLabel: undefined,
    errorLine: null,
    submitOnModEnter: false,
  }
);

const model = defineModel<string>({ default: '' });

const emit = defineEmits<{
  /** The editor gained or lost focus. */
  focus: [];
  blur: [];
  /** Ctrl/Cmd+Enter with `submitOnModEnter`. */
  submit: [];
}>();

const hostRef = useTemplateRef<HTMLDivElement>('host');
const view = shallowRef<EditorView | null>(null);

// Props that change the editor's setup live in compartments: reconfiguring
// them keeps the document, the selection and the undo history.
const setup = {
  language: new Compartment(),
  completions: new Compartment(),
  editable: new Compartment(),
  gutters: new Compartment(),
  wrap: new Compartment(),
  placeholder: new Compartment(),
  label: new Compartment(),
  extra: new Compartment(),
};

// The error line is a decoration rather than a class on a gutter element: the
// document changes under it, and CodeMirror maps a decoration through them.
const setErrorLine = StateEffect.define<number | null>();
const errorLineField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(marks, tr) {
    for (const effect of tr.effects) {
      if (!effect.is(setErrorLine)) continue;
      const line = effect.value;
      if (!line || line < 1 || line > tr.state.doc.lines) return Decoration.none;
      return Decoration.set([
        Decoration.line({ class: 'cm-errorLine' }).range(tr.state.doc.line(line).from),
      ]);
    }
    return marks.map(tr.changes);
  },
  provide: (field) => EditorView.decorations.from(field),
});

const editableExt = () => [
  EditorView.editable.of(!props.readonly),
  EditorState.readOnly.of(props.readonly),
];
const gutterExt = () =>
  props.lineNumbers ? [lineNumbersExt(), highlightActiveLineGutter(), foldGutter()] : [];
const labelExt = () =>
  EditorView.contentAttributes.of({
    ...(props.ariaLabel ? { 'aria-label': props.ariaLabel } : {}),
    'aria-multiline': 'true',
    ...(props.readonly ? { 'aria-readonly': 'true' } : {}),
  });

function create() {
  if (!hostRef.value) return;
  view.value = new EditorView({
    parent: hostRef.value,
    state: EditorState.create({
      doc: model.value,
      extensions: [
        highlightSpecialChars(),
        history(),
        drawSelection(),
        EditorState.allowMultipleSelections.of(true),
        indentOnInput(),
        indentUnit.of('  '),
        syntaxHighlighting(nxCodeHighlightStyle, { fallback: true }),
        bracketMatching(),
        closeBrackets(),
        highlightActiveLine(),
        highlightSelectionMatches(),
        // Ahead of defaultKeymap, whose Mod-Enter inserts a blank line; read
        // at key time, so toggling the prop needs no reconfiguration.
        Prec.highest(
          keymap.of([
            {
              key: 'Mod-Enter',
              run: () => {
                if (!props.submitOnModEnter) return false;
                emit('submit');
                return true;
              },
            },
          ])
        ),
        keymap.of([
          ...closeBracketsKeymap,
          ...defaultKeymap,
          ...searchKeymap,
          ...historyKeymap,
          ...foldKeymap,
          ...completionKeymap,
          indentWithTab,
        ]),
        errorLineField,
        nxCodeTheme,
        setup.language.of(languageExtension(props.language)),
        setup.completions.of(completionExtension(props.completions)),
        setup.editable.of(editableExt()),
        setup.gutters.of(gutterExt()),
        setup.wrap.of(props.wrap ? EditorView.lineWrapping : []),
        setup.placeholder.of(props.placeholder ? placeholderExt(props.placeholder) : []),
        setup.label.of(labelExt()),
        setup.extra.of([...props.extensions]),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) model.value = update.state.doc.toString();
          if (update.focusChanged) {
            if (update.view.hasFocus) emit('focus');
            else emit('blur');
          }
        }),
      ],
    }),
  });
  applyErrorLine();
}

function reconfigure(compartment: Compartment, extension: Extension) {
  view.value?.dispatch({ effects: compartment.reconfigure(extension) });
}

function applyErrorLine() {
  view.value?.dispatch({ effects: setErrorLine.of(props.errorLine ?? null) });
}

onMounted(create);
onBeforeUnmount(() => {
  view.value?.destroy();
  view.value = null;
});

watch(
  () => props.language,
  (language) => reconfigure(setup.language, languageExtension(language))
);
watch(
  () => props.completions,
  (completions) => reconfigure(setup.completions, completionExtension(completions))
);
watch(
  () => props.readonly,
  () => {
    reconfigure(setup.editable, editableExt());
    reconfigure(setup.label, labelExt());
  }
);
watch(
  () => props.lineNumbers,
  () => reconfigure(setup.gutters, gutterExt())
);
watch(
  () => props.wrap,
  (wrap) => reconfigure(setup.wrap, wrap ? EditorView.lineWrapping : [])
);
watch(
  () => props.placeholder,
  (text) => reconfigure(setup.placeholder, text ? placeholderExt(text) : [])
);
watch(
  () => props.ariaLabel,
  () => reconfigure(setup.label, labelExt())
);
watch(
  () => props.extensions,
  (extensions) => reconfigure(setup.extra, [...extensions])
);
watch(() => props.errorLine, applyErrorLine);

// An outside change (loading a file, resetting) replaces the document; the
// editor's own edits already came through the listener and must not loop.
watch(model, (value) => {
  const current = view.value;
  if (!current || current.state.doc.toString() === value) return;
  current.dispatch({ changes: { from: 0, to: current.state.doc.length, insert: value ?? '' } });
  applyErrorLine();
});

defineExpose({
  /** The CodeMirror view, for callers that need to dispatch themselves. */
  view,
  /** Moves the focus into the editor. */
  focus: () => view.value?.focus(),
});
</script>

<template>
  <div
    ref="host"
    class="nx-code-editor"
    :class="{ 'is-readonly': props.readonly }"
    :style="{ '--code-min': props.minHeight, '--code-max': props.maxHeight }"
    data-testid="nx-code-editor"
  />
</template>

<style scoped>
.nx-code-editor {
  display: flex;
  min-width: 0;
  height: var(--code-min);
  min-height: var(--code-min);
  max-height: var(--code-max);
  overflow: hidden;
  border: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
  resize: vertical;
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-code-editor:focus-within {
  border-color: var(--nxt-brand-border);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-code-editor :deep(.cm-editor) {
  flex: 1 1 auto;
  min-width: 0;
}
.nx-code-editor.is-readonly {
  background: var(--nxt-surface-sunken-bg);
}
.nx-code-editor.is-readonly :deep(.cm-content) {
  color: var(--nxt-fg-subtle);
}
@media (prefers-reduced-motion: reduce) {
  .nx-code-editor {
    transition: none;
  }
}
</style>
